import { c as _c } from "react/compiler-runtime";
import { Avatar, Box, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import React from "react";
import { categoryLabels } from "#/entities/category/model/categoryLabels";
import { getDifficultyColor } from "#/entities/difficulty/lib/getDifficultyColor";
import { difficultyLabels } from "#/entities/difficulty/model/difficultyLabels";
import type { RouterOutputs } from "#/shared/api";
import { getImageUrl } from "#/shared/lib";
import { NewLabel } from "#/shared/ui/atoms/NewLabel";

// Support both allBrief and browseProjects types (they have compatible structures)
type ProjectBrief = RouterOutputs["project"]["allBrief"][number] | RouterOutputs["project"]["browseProjects"]["projects"][number];
type ProjectBrowserItemProps = {
  project: ProjectBrief;
  isSelected: boolean;
  onClick: () => void;
};
const ProjectBrowserItemComponent: React.FC<ProjectBrowserItemProps> = t0 => {
  const $ = _c(58);
  const {
    project,
    isSelected,
    onClick
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== onClick) {
    t1 = ev => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        onClick();
      }
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleKeyDown = t1;
  let t2;
  if ($[2] !== project.difficulty || $[3] !== theme) {
    t2 = project.difficulty ? getDifficultyColor(theme, project.difficulty) : undefined;
    $[2] = project.difficulty;
    $[3] = theme;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const difficultyColor = t2;
  const t3 = `Select project ${project.title}`;
  const t4 = isSelected ? theme.palette.action.selected : "transparent";
  const t5 = `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`;
  const t6 = isSelected ? theme.palette.primary.main : "transparent";
  let t7;
  if ($[5] !== theme.palette.action.hover) {
    t7 = {
      backgroundColor: theme.palette.action.hover
    };
    $[5] = theme.palette.action.hover;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = `2px solid ${theme.palette.primary.main}`;
  let t9;
  if ($[7] !== t8) {
    t9 = {
      outline: t8,
      outlineOffset: -2
    };
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6 || $[12] !== t7 || $[13] !== t9) {
    t10 = {
      justifyContent: "space-between",
      alignItems: "center",
      height: 76,
      p: 1.75,
      px: 2,
      cursor: "pointer",
      borderRadius: 1.5,
      backgroundColor: t4,
      border: t5,
      borderColor: t6,
      "&:hover": t7,
      "&:focus-visible": t9,
      transition: "background-color 0.15s ease-out"
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      alignItems: "center",
      flex: 1,
      minWidth: 0
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Box sx={{
      width: 20,
      height: 20,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }} aria-label="Completion status" />;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      flex: 1,
      minWidth: 0
    };
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      alignItems: "center",
      flexWrap: "nowrap"
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = isSelected ? 600 : 500;
  const t16 = isSelected ? theme.palette.text.primary : theme.palette.text.primary;
  let t17;
  if ($[19] !== t15 || $[20] !== t16) {
    t17 = {
      fontWeight: t15,
      fontSize: "0.95rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: t16
    };
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== project.title || $[23] !== t17) {
    t18 = <Typography variant="body1" sx={t17}>{project.title}</Typography>;
    $[22] = project.title;
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== project.createdAt || $[26] !== project.isNew) {
    t19 = project.isNew && <NewLabel createdAt={project.createdAt} />;
    $[25] = project.createdAt;
    $[26] = project.isNew;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t18 || $[29] !== t19) {
    t20 = <Stack direction="row" spacing={1} sx={t14}>{t18}{t19}</Stack>;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      alignItems: "center"
    };
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      color: "text.secondary",
      fontSize: "0.75rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    };
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  const t23 = categoryLabels[project.category];
  let t24;
  if ($[33] !== t23) {
    t24 = <Stack direction="row" spacing={1} sx={t21}><Typography variant="caption" sx={t22}>{t23}</Typography></Stack>;
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== t20 || $[36] !== t24) {
    t25 = <Stack direction="row" spacing={1.5} sx={t11}>{t12}<Stack direction="column" spacing={0.5} sx={t13}>{t20}{t24}</Stack></Stack>;
    $[35] = t20;
    $[36] = t24;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      alignItems: "center",
      minWidth: 0,
      flexShrink: 0
    };
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] !== difficultyColor || $[40] !== project.difficulty || $[41] !== theme.palette.divider || $[42] !== theme.palette.text.secondary) {
    t27 = project.difficulty && <Chip label={difficultyLabels[project.difficulty]} size="small" sx={{
      height: 24,
      fontSize: "0.7rem",
      fontWeight: 600,
      backgroundColor: difficultyColor ? `${difficultyColor}20` : "transparent",
      color: difficultyColor || theme.palette.text.secondary,
      border: difficultyColor ? `1px solid ${difficultyColor}40` : `1px solid ${theme.palette.divider}`,
      "& .MuiChip-label": {
        px: 1
      }
    }} />;
    $[39] = difficultyColor;
    $[40] = project.difficulty;
    $[41] = theme.palette.divider;
    $[42] = theme.palette.text.secondary;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== project.author || $[45] !== theme.palette.divider) {
    t28 = project.author?.bucketImage && <Tooltip title={`Author: ${project.author.name}`} arrow={true}><Avatar src={getImageUrl(project.author.bucketImage)} alt={`${project.author.name} avatar`} sx={{
        height: 28,
        width: 28,
        border: `2px solid ${theme.palette.divider}`
      }} /></Tooltip>;
    $[44] = project.author;
    $[45] = theme.palette.divider;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== t27 || $[48] !== t28) {
    t29 = <Stack direction="row" spacing={1.5} sx={t26}>{t27}{t28}</Stack>;
    $[47] = t27;
    $[48] = t28;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== handleKeyDown || $[51] !== isSelected || $[52] !== onClick || $[53] !== t10 || $[54] !== t25 || $[55] !== t29 || $[56] !== t3) {
    t30 = <Stack direction="row" spacing={1.5} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0} role="button" aria-selected={isSelected} aria-label={t3} sx={t10}>{t25}{t29}</Stack>;
    $[50] = handleKeyDown;
    $[51] = isSelected;
    $[52] = onClick;
    $[53] = t10;
    $[54] = t25;
    $[55] = t29;
    $[56] = t3;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  return t30;
};
ProjectBrowserItemComponent.displayName = "ProjectBrowserItem";
export const ProjectBrowserItem = React.memo(ProjectBrowserItemComponent, (prevProps, nextProps) => {
  // Custom comparison: return true if props are equal (skip re-render)
  // Only re-render if project id, isSelected, or onClick reference changes
  return prevProps.project.id === nextProps.project.id && prevProps.isSelected === nextProps.isSelected && prevProps.onClick === nextProps.onClick && prevProps.project.slug === nextProps.project.slug && prevProps.project.title === nextProps.project.title && prevProps.project.category === nextProps.project.category && prevProps.project.difficulty === nextProps.project.difficulty;
});